import { useQuery } from "@tanstack/react-query";

import { readScoreDecisionExplanation } from "@/shared/api";
import type { ScoreDecisionExplanationResponse } from "@/shared/api";
import { statusOf } from "@/shared/http/errors";

type ExplanationState =
  | { readonly status: "loading" }
  | { readonly status: "forbidden" }
  | { readonly status: "unavailable" }
  | { readonly status: "unreadable" }
  | { readonly status: "ready"; readonly data: ScoreDecisionExplanationResponse }
  | { readonly status: "stale"; readonly data: ScoreDecisionExplanationResponse };

function stateOf(query: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: ScoreDecisionExplanationResponse | undefined;
}): ExplanationState {
  if (query.data !== undefined) {
    return query.isError
      ? { status: "stale", data: query.data }
      : { status: "ready", data: query.data };
  }
  if (query.isPending) return { status: "loading" };
  const status = statusOf(query.error);
  if (status === 403) return { status: "forbidden" };
  if (status === 503) return { status: "unavailable" };
  return { status: "unreadable" };
}

export function useCycleScoreExplanation(
  cycleId: string | null,
  enabled: boolean,
): ExplanationState {
  return stateOf(
    useQuery({
      queryKey: ["score-cycle-explanation", cycleId],
      enabled: enabled && cycleId !== null,
      queryFn: async (): Promise<ScoreDecisionExplanationResponse> => {
        if (cycleId === null) throw new Error("cycle identity is unavailable");
        const { data, response } = await readScoreDecisionExplanation({
          path: { cycle_id: cycleId },
        });
        if (data === undefined) {
          throw Object.assign(new Error("score decision explanation unreadable"), {
            status: response?.status,
          });
        }
        return data;
      },
      refetchInterval: 30_000,
      retry: false,
    }),
  );
}
